import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useTranslated } from "../lib/useTranslated";
import { sanitizeArticleHtml } from "../lib/sanitizeHtml";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function buildUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  if (path.startsWith("/api/files/")) return `${BACKEND_URL}${path}`;
  return path;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * `/giornale/:slug` — public article page.
 *
 * Renders the article as a longform magazine spread: overline · title ·
 * subtitle · author line · hero image · WYSIWYG body. The body_html comes
 * from Quill so it's already sanitised (subset of tags). We render it
 * inside a styled `.gd-journal-body` container which handles typography,
 * spacing and inline images automatically.
 */
export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Hooks MUST run in the same order every render — call the translation
  // hooks unconditionally with fallbacks to empty strings, then rely on
  // the early returns below to skip rendering when the article is missing.
  const translatedTitle = useTranslated(article?.title || "");
  const translatedSubtitle = useTranslated(article?.subtitle || "");
  const translatedBody = useTranslated(article?.body_html || "");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: "#FFFFFF", color: "#6E6E73", fontSize: 13 }}
      >
        Caricamento…
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#FFFFFF" }}
      >
        <div className="text-center max-w-md">
          <p
            className="text-[11px] uppercase mb-3"
            style={{
              color: "#FF2800",
              letterSpacing: "0.3em",
              fontWeight: 500,
            }}
          >
            Articolo non trovato
          </p>
          <h1
            className="text-3xl font-light mb-6"
            style={{ color: "#1D1D1F" }}
          >
            Questo articolo non esiste o è stato rimosso.
          </h1>
          <Link to="/giornale" className="gd-pill gd-pill-primary">
            Torna al Giornale
          </Link>
        </div>
      </div>
    );
  }

  const cover = buildUrl(article.cover_image_url);

  return (
    <article
      className="min-h-screen pt-24 sm:pt-28 pb-24"
      style={{ background: "#FFFFFF" }}
      data-testid="journal-article-page"
    >
      {/* Back link — tiny, top-left */}
      <div className="px-6 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/giornale"
            className="text-[11px] uppercase inline-flex items-center gap-1"
            style={{
              color: "#6E6E73",
              letterSpacing: "0.28em",
              fontWeight: 500,
            }}
            data-testid="journal-back"
          >
            <ChevronLeft size={13} strokeWidth={1.5} />
            Giornale
          </Link>
        </div>
      </div>

      {/* Editorial header — overline + big serif title + standfirst + byline */}
      <div className="px-6 sm:px-12">
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mx-auto mt-8 sm:mt-12 mb-10 sm:mb-16 text-center"
        >
          <p
            className="text-[11px] uppercase mb-6"
            style={{
              color: "#FF2800",
              letterSpacing: "0.4em",
              fontWeight: 500,
            }}
          >
            Giornale · Storia
          </p>
          <h1
            className="gd-article-title text-4xl sm:text-6xl lg:text-7xl mb-6 text-balance"
            data-testid="journal-article-title"
          >
            {translatedTitle}
          </h1>
          {article.subtitle && (
            <p className="gd-article-standfirst text-xl sm:text-2xl max-w-2xl mx-auto">
              {translatedSubtitle}
            </p>
          )}

          <div className="gd-article-byline mt-10 sm:mt-14 max-w-md mx-auto">
            <span className="rule" />
            <span>
              {article.author ? (
                <>
                  Di <span className="accent">{article.author}</span>
                </>
              ) : (
                "Redazione"
              )}
              <span style={{ opacity: 0.5, margin: "0 6px" }}>·</span>
              2 min
            </span>
            <span className="rule" />
          </div>
          <p
            className="text-[11px] uppercase mt-3"
            style={{ color: "#6E6E73", letterSpacing: "0.28em" }}
          >
            {fmtDate(article.published_at || article.created_at)}
          </p>
        </motion.header>
      </div>

      {/* Full-bleed cinematic hero image */}
      {cover && (
        <div className="mb-14 sm:mb-20">
          <div
            className="w-full overflow-hidden"
            style={{
              aspectRatio: "16 / 9",
              background: "#F5F5F7",
            }}
          >
            <img
              src={cover}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      {/* Body */}
      <div className="px-6 sm:px-12 overflow-hidden">
        <div
          className="max-w-2xl mx-auto gd-journal-body"
          style={{ minWidth: 0 }}
          data-testid="journal-article-body"
          dangerouslySetInnerHTML={{
            __html: sanitizeArticleHtml(translatedBody || ""),
          }}
        />

        {/* End-of-article diamond marker */}
        <div className="max-w-2xl mx-auto gd-journal-endmark" aria-hidden>
          ◆ ◆ ◆
        </div>

        <div
          className="max-w-2xl mx-auto mt-10 sm:mt-14 pt-8 border-t"
          style={{ borderColor: "rgba(0,0,0,0.08)" }}
        >
          {article.author && (
            <p
              className="text-sm"
              style={{ color: "#6E6E73", fontStyle: "italic" }}
              data-testid="journal-article-signature"
            >
              — {article.author}
            </p>
          )}
          <div className="mt-6">
            <Link to="/giornale" className="gd-pill gd-pill-primary">
              Altri articoli
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
